import React, { useEffect, useState, useCallback } from 'react';
import accessLocalStorage from '../../services/accessLocalStorage';
import './config-styles.css';

const getApiBase = async () => {
  const { ipRequest } = await accessLocalStorage.getUserLocalStorage();
  const ipClean = ipRequest.split('/')[2];
  const host = ipClean.split(':')[0];
  const protocol = window.location.protocol === 'https:' ? 'https:' : 'http:';
  return `${protocol}//${host}`;
};

const getTenantId = async () => {
  const { user: { idtenant } } = await accessLocalStorage.getUserLocalStorage();
  return String(idtenant);
};

const getToken = async () => {
  const { token } = await accessLocalStorage.getUserLocalStorage();
  return token;
};

function SkillsConfig() {
  const [loading, setLoading] = useState(true);
  const [skills, setSkills] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingSkill, setEditingSkill] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'technical',
  });

  const categories = [
    { value: 'technical', label: 'Técnico' },
    { value: 'sales', label: 'Vendas' },
    { value: 'support', label: 'Suporte' },
    { value: 'billing', label: 'Financeiro' },
    { value: 'language', label: 'Idioma' },
    { value: 'product', label: 'Produto' },
    { value: 'other', label: 'Outro' },
  ];

  const fetchSkills = useCallback(async () => {
    try {
      setLoading(true);
      const apiBase = await getApiBase();
      const tenantId = await getTenantId();
      const token = await getToken();

      const response = await fetch(`${apiBase}/v2/callcenter/skills`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-tenant-id': tenantId,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setSkills(data.skills || data || []);
      }
    } catch (e) {
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSkills();
  }, [fetchSkills]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const apiBase = await getApiBase();
      const tenantId = await getTenantId();
      const token = await getToken();

      const url = editingSkill
        ? `${apiBase}/v2/callcenter/skills/${encodeURIComponent(editingSkill.skillKey)}`
        : `${apiBase}/v2/callcenter/skills`;

      const method = editingSkill ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'x-tenant-id': tenantId,
        },
        body: JSON.stringify({
          ...formData,
          skillKey: formData.name.toLowerCase().replace(/\s+/g, '-'),
        }),
      });

      if (response.ok) {
        setShowModal(false);
        setEditingSkill(null);
        resetForm();
        fetchSkills();
      } else {
        const errorData = await response.json().catch(() => ({}));
        alert(`Erro ao salvar skill: ${errorData.message || response.statusText}`);
      }
    } catch (e) {
      console.error('Erro ao salvar skill:', e);
      alert('Erro ao salvar skill. Verifique sua conexão.');
    }
  };

  const handleDelete = async (skillKey) => {
    if (!window.confirm('Tem certeza que deseja excluir esta skill?')) return;

    try {
      const apiBase = await getApiBase();
      const tenantId = await getTenantId();
      const token = await getToken();

      const response = await fetch(`${apiBase}/v2/callcenter/skills/${encodeURIComponent(skillKey)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-tenant-id': tenantId,
        },
      });

      if (response.ok) {
        fetchSkills();
      } else {
        const errorData = await response.json().catch(() => ({}));
        alert(`Erro ao excluir skill: ${errorData.message || response.statusText}`);
      }
    } catch (e) {
      console.error('Erro ao excluir skill:', e);
      alert('Erro ao excluir skill. Verifique sua conexão.');
    }
  };

  const openEditModal = (skill) => {
    setEditingSkill(skill);
    setFormData({
      name: skill.name || '',
      description: skill.description || '',
      category: skill.category || 'technical',
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      category: 'technical',
    });
  };

  const openNewModal = () => {
    setEditingSkill(null);
    resetForm();
    setShowModal(true);
  };

  const getCategoryLabel = (value) => {
    const cat = categories.find((c) => c.value === value);
    return cat ? cat.label : value;
  };

  const getCategoryColor = (category) => {
    const colors = {
      technical: '#3b82f6',
      sales: '#22c55e',
      support: '#f59e0b',
      billing: '#8b5cf6',
      language: '#ec4899',
      product: '#06b6d4',
      other: '#6b7280',
    };
    return colors[category] || colors.other;
  };

  return (
    <div className="ccv2-config-container">
      <div className="ccv2-config-header">
        <div className="ccv2-config-title-area">
          <h1>Configuração de Skills</h1>
          <p>Gerencie as habilidades dos agentes</p>
        </div>
        <button className="ccv2-btn ccv2-btn-primary" onClick={openNewModal}>
          + Nova Skill
        </button>
      </div>

      {loading ? (
        <div className="ccv2-config-loading">
          <div className="ccv2-spinner"></div>
          <p>Carregando skills...</p>
        </div>
      ) : (
        <div className="ccv2-config-card">
          <div className="ccv2-skills-grid">
            {skills.length === 0 ? (
              <div className="ccv2-empty-state">
                <span className="ccv2-empty-icon">🎯</span>
                <h3>Nenhuma skill cadastrada</h3>
                <p>Crie skills para classificar as habilidades dos agentes</p>
                <button className="ccv2-btn ccv2-btn-primary" onClick={openNewModal}>
                  Criar primeira skill
                </button>
              </div>
            ) : (
              skills.map((skill) => (
                <div key={skill.skillKey || skill.id_callcenter_skill} className="ccv2-skill-card">
                  <div className="ccv2-skill-header">
                    <span
                      className="ccv2-skill-category"
                      style={{ backgroundColor: getCategoryColor(skill.category) }}
                    >
                      {getCategoryLabel(skill.category)}
                    </span>
                    <div className="ccv2-skill-actions">
                      <button
                        className="ccv2-btn-icon ccv2-btn-edit"
                        onClick={() => openEditModal(skill)}
                        title="Editar"
                      >
                        ✏️
                      </button>
                      <button
                        className="ccv2-btn-icon ccv2-btn-delete"
                        onClick={() => handleDelete(skill.skillKey)}
                        title="Excluir"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                  <h3 className="ccv2-skill-name">{skill.name}</h3>
                  <p className="ccv2-skill-description">{skill.description || 'Sem descrição'}</p>
                  <div className="ccv2-skill-footer">
                    <span className="ccv2-skill-key">{skill.skillKey}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {showModal && (
        <div className="ccv2-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="ccv2-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ccv2-modal-header">
              <h2>{editingSkill ? 'Editar Skill' : 'Nova Skill'}</h2>
              <button className="ccv2-modal-close" onClick={() => setShowModal(false)}>
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="ccv2-modal-body">
                <div className="ccv2-form-group">
                  <label>Nome da Skill</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Ex: Suporte Linux"
                    required
                  />
                </div>

                <div className="ccv2-form-group">
                  <label>Descrição</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Descreva a habilidade..."
                    rows="3"
                  />
                </div>

                <div className="ccv2-form-group">
                  <label>Categoria</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  >
                    {categories.map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="ccv2-modal-footer">
                <button type="button" className="ccv2-btn ccv2-btn-secondary" onClick={() => setShowModal(false)}>
                  Cancelar
                </button>
                <button type="submit" className="ccv2-btn ccv2-btn-primary">
                  {editingSkill ? 'Salvar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default SkillsConfig;
